// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const quotes = [
  "I wish my quotes would just send themselves when someone fills in the form.",
  "We copy the same data between three spreadsheets every single week.",
  "I need something that follows up with leads when I'm on the job and can't check my phone.",
  "Can I see which jobs are actually making money — without digging through invoices?",
  "I want customers to book online, but it has to work around how we actually schedule things.",
  "Our team uses five different apps and nothing talks to anything else.",
  "Someone needs to chase overdue invoices so I don't have to do it myself.",
  "I've been meaning to fix our Google ranking for two years. I just don't know where to start.",
];

export default function WeBuildThat() {
  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="codeteki-pill mb-6">Custom Tool Development</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-black mb-4">
            "If only there was a tool that could do that…"
          </h2>
          <p className="text-lg text-[#52525b]">
            Every business owner has a list. Problems they've put up with for years because no off-the-shelf software quite fits. We build the thing on that list.
          </p>
        </div>

        {/* Quote grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {quotes.map((q, i) => (
            <div
              key={i}
              className="group relative rounded-2xl border border-[#e5e7eb] bg-[#fafafa] p-5 hover:border-[#f9cb07] hover:bg-white hover:shadow-[0_8px_30px_rgba(249,203,7,0.12)] transition-all duration-200"
            >
              <span className="text-5xl font-serif leading-none text-[#f9cb07] select-none">"</span>
              <p className="mt-1 text-sm text-[#374151] leading-relaxed italic">{q}</p>
            </div>
          ))}
        </div>

        {/* Punchline */}
        <div className="mt-10 max-w-4xl mx-auto rounded-2xl bg-[#0f172a] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-2xl md:text-3xl font-bold text-white leading-snug">
              We build exactly that.
            </p>
            <p className="mt-2 text-white/50 text-sm md:text-base max-w-lg">
              Scoped, priced, and shipped in weeks — not months. One Melbourne-based team from brief to launch. No outsourcing, no middlemen.
            </p>
          </div>
          <Button
            asChild
            className="shrink-0 bg-[#f9cb07] text-black font-semibold text-base px-8 py-4 hover:bg-[#e6b800] rounded-xl"
          >
            <a href="/get-started">
              Tell us what you need
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>

        <p className="text-center text-[#9ca3af] text-sm mt-6">
          Not sure if we can build it?{" "}
          <a href="/contact" className="text-[#0f172a] font-semibold hover:text-[#f9cb07] transition-colors">
            Ask us anyway →
          </a>
        </p>

      </div>
    </section>
  );
}
